import classNames from "classnames";
import { motion } from "framer-motion";
import { ReactNode, useCallback } from "react";
import { Difficulty } from "shared/games/Difficulty";
import { Props as ButtonProps } from "ui/atoms/Button/Button";
import Shape from "ui/atoms/Shape/Shape";
import Switch from "ui/atoms/Switch/Switch";
import DifficultyDescriptions from "./DifficultyDescriptions";
import getCompletionHistoryIcon from "./getCompletionHistoryIcon";
import {
  BaseGameDescription,
  PointsByDifficulty,
} from "shared/games/allTechniqueGameDescriptions";
import usePoints from "./usePoints";
import api from "util/api";
import {
  apiRecombeeUpdateUserPreferences,
  ApiRecombeeUpdateUserPreferencesParams,
  ApiRecombeeUpdateUserPreferencesResponse,
} from "apiRoutes/apiRecombee";

export type History = { [difficulty in Difficulty]?: { correct: boolean } };

export interface Props {
  value: Difficulty;
  onChange: (value: Difficulty) => void;
  history?: History;
  games: BaseGameDescription[];
  points: PointsByDifficulty;
  pointsWhenCompleted: PointsByDifficulty;
  pointsStyle?: "shape" | "in-description";
  mobileAction?: {
    // Should be something like md:hidden or false for it to always be present
    breakpointHidden: string | false;
    button: (props: Partial<ButtonProps>) => ReactNode;
  };
}

export default function DifficultSelector(props: Props) {
  const { value, history, games, mobileAction, pointsStyle } = props;
  const showPointShape = pointsStyle === "shape";
  const points = usePoints(props);
  const onChange = useCallback(
    (value: Difficulty) => {
      props.onChange(value);

      api<
        ApiRecombeeUpdateUserPreferencesResponse,
        ApiRecombeeUpdateUserPreferencesParams
      >(apiRecombeeUpdateUserPreferences, "POST", {
        body: { difficulty_preference: value },
      });
    },
    [props]
  );

  return (
    <article
      className={classNames(
        "w-full rounded border-2 border-slate-300 bg-black"
      )}
    >
      <div className="p-2">
        <DifficultyDescriptions
          games={games}
          difficulty={value}
          points={showPointShape ? undefined : points}
        />

        <div className="flex items-center justify-between">
          <div className="mr-2 overflow-auto">
            <Switch
              value={value}
              onChange={(value) => onChange(value)}
              items={Object.values(Difficulty).map((difficulty) => ({
                label: difficultyLabel(difficulty),
                value: difficulty,
                icon: getCompletionHistoryIcon({ history, difficulty }),
              }))}
            />
          </div>

          {showPointShape && (
            <div className="flex flex-1 items-center justify-end text-white">
              <div>
                <Shape
                  shape="circle"
                  sizeClassName="w-[30px]"
                  theme="yellow"
                  type="content"
                  fullScreenOverlay={({ childrenVariants }) => (
                    <motion.div
                      className="mx-auto max-w-md px-4 py-20 text-4xl"
                      variants={childrenVariants}
                    >
                      ToDo
                    </motion.div>
                  )}
                >
                  {points?.[value] ?? 0}
                </Shape>
              </div>
            </div>
          )}
        </div>
      </div>

      {mobileAction && (
        <div className={classNames(mobileAction.breakpointHidden)}>
          <hr className="border-1 mb-0.5 border-dashed border-slate-300" />
          <div className="p-2">{mobileAction.button({ block: true })}</div>
        </div>
      )}
    </article>
  );
}

function difficultyLabel(difficulty: Difficulty): string {
  return difficulty === Difficulty.medium ? "med" : difficulty;
}
